/**
 * The memorial beat (08 §2.9, §5.4): loss gets ceremony. The screen goes quiet - darker, a
 * slow fade, no stamp thumping down - and shows who was lost, how, what he had done, and his
 * epitaph. Input is held for three seconds so it cannot be skipped by a finger still firing.
 * PHOTOS: when the moment he fell was photographed, the picture stands under his name.
 */

import './ceremony.css';
import type { MedalId, MemorialEntry, RangerRecord } from '../../../meta/types';
import type { AftermathInput } from '../context';
import { box, el } from '../dom';
import { companyPicture, photoImage, pictureSlot } from '../photoView';
import { fullName, rankAbbr } from '../text';
import { bust } from '../widgets/dossierCard';
import { rankInsignia } from '../widgets/insignia';
import { ribbonRack } from '../widgets/ribbonRack';
import { publishOffer } from './publishOffer';
import type { Step } from './shell';
import { continueButton, holdInput, stepShell } from './shell';

/** How long CONTINUE is held back on a death (08 §2.9). */
export const MEMORIAL_INPUT_DELAY_MS = 3000;

export interface Mourned {
  ranger: RangerRecord;
  /** The wall's entry; null when the layer reported a death without one. */
  entry: MemorialEntry | null;
  /** He is the ranger who flew this mission (a lost POW is not). */
  deployed: boolean;
}

/** Who this mission cost, if anyone. Practice costs nobody: there is no ranger to lose. Pure. */
export function mourned(input: AftermathInput): Mourned | null {
  const after = input.aftermath;
  if (after === null) return null;
  if (after.memorial !== null) {
    const entry = after.memorial;
    return { ranger: entry.ranger, entry, deployed: entry.ranger.id === after.ranger.id };
  }
  return input.result.outcome === 'kia' ? { ranger: after.ranger, entry: null, deployed: true } : null;
}

/** His medals with this mission's posthumous awards added, no duplicates. Pure. */
export function withPosthumous(held: readonly MedalId[], awarded: readonly MedalId[]): MedalId[] {
  return [...held, ...awarded.filter((m) => !held.includes(m))];
}

function fact(key: string, value: string): HTMLElement {
  return box('menu-grave-fact', el('span', 'menu-grave-key', key), el('span', 'menu-grave-value', value));
}

export const memorialBeat: Step = (env, next) => {
  const lost = mourned(env.input);
  const shell = stepShell('IN MEMORIAM', 'menu-after-beat');
  if (lost === null) return { el: shell.el, focus: null };
  const { ranger, entry } = lost;
  const awarded = lost.deployed ? (env.input.aftermath?.medals ?? []) : [];

  env.screen.classList.add('menu-after-grave');
  const fate = entry?.fate === 'lost' ? 'POW - NEVER RECOVERED' : 'KILLED IN ACTION';
  const names = box(
    'menu-grave-names',
    el('div', 'menu-grave-name', `${rankAbbr(ranger.rank)} ${fullName(ranger)}`),
    el('div', 'menu-grave-callsign', ranger.callsign.trim() === '' ? '' : `"${ranger.callsign}"`),
    el('div', 'menu-grave-fate', fate),
  );
  shell.content.append(
    box('menu-grave-head', box('menu-grave-portrait', bust(ranger.seed, true)), rankInsignia(ranger.rank), names),
  );
  // PHOTOS: the moment he fell, under his name. Drawn when it arrives - the beat never waits on it.
  const source = entry === null ? null : companyPicture(env.ctx, entry);
  const picture = pictureSlot('menu-grave-photo', source, (url, fail) => photoImage('menu-grave-photo-img', url, fail));
  if (picture !== null) shell.content.append(picture);

  const facts = el('div', 'menu-grave-facts');
  if (entry !== null) {
    facts.append(fact('OPERATION', entry.operation), fact('MISSION', entry.mission), fact('CAUSE', entry.cause));
  } else {
    facts.append(fact('MISSION', env.ctx.describe(env.input.deployment.params.archetype).name));
  }
  const rec = ranger.record;
  facts.append(fact('SERVICE', `${rec.missions} MISSIONS - ${rec.successes} ACCOMPLISHED - ${rec.kills} KILLS`));
  shell.content.append(facts, ribbonRack(withPosthumous(ranger.medals, awarded), env.ctx.describe));
  if (awarded.length > 0) {
    const awards = awarded.map((m) => env.ctx.describe(m).name).join(', ');
    shell.content.append(el('div', 'menu-grave-posthumous', `AWARDED POSTHUMOUSLY: ${awards}`));
  }
  if (entry !== null && entry.epitaph.trim() !== '') {
    shell.content.append(el('div', 'menu-grave-epitaph menu-prose', entry.epitaph));
  }
  const wall = el('div', 'menu-grave-wall', entry === null ? '' : 'ADDED TO THE MEMORIAL WALL');
  wall.classList.add('menu-grave-wait');
  shell.content.append(wall);

  // The offer to publish him (WALL-contracts §1) waits out the same three seconds as CONTINUE: the
  // one question this game ever asks about sharing is not one to answer with a finger still firing.
  const offer = publishOffer(env.ctx, entry);
  if (offer !== null) {
    offer.el.classList.add('menu-grave-wait');
    // Unpressable while it is unseen, not merely unfocused: an arrow key and a Space still held from
    // the mission would otherwise find its first button - which, with a picture, sends the picture.
    offer.el.inert = true;
    shell.content.append(offer.el);
  }

  const go = continueButton(next);
  shell.actions.append(go);
  holdInput(env.timers, go, MEMORIAL_INPUT_DELAY_MS, () => {
    wall.classList.remove('menu-grave-wait');
    if (offer === null) return;
    offer.el.classList.remove('menu-grave-wait');
    offer.el.inert = false;
    // The question is the last thing on the beat, and a picture above it can push it below the fold.
    offer.el.scrollIntoView({ block: 'nearest' });
  });
  return {
    el: shell.el,
    focus: go,
    // A death keeps the screen quiet to the end; a lost prisoner hands it back to the living.
    leave: () => {
      if (!env.deceased) env.screen.classList.remove('menu-after-grave');
    },
  };
};
